import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { RefreshCw, ChevronDown, ChevronUp, CheckCircle, FileText } from 'lucide-react';
import { fmtPatron } from '../utils/dates';

// ── Revisión de cargas ─────────────────────────────────────
// Historial de importaciones masivas (PDF IA / Excel) con el detalle por
// registro. Permite validar qué entró, detectar duplicados y posibles
// pólizas mal asignadas, y marcar cada carga como revisada.

const ST = {
  created:     { label: 'Creada',       class: 'badge-green' },
  duplicate:   { label: 'Duplicado',    class: 'badge-gray'  },
  client_only: { label: 'Solo cliente', class: 'badge-blue'  },
  skipped:     { label: 'Omitida',      class: 'badge-amber' },
  error:       { label: 'Error',        class: 'badge-red'   },
};

const SOURCE_LABEL = { 'pdf-ia': 'PDF con IA', excel: 'Excel' };

async function apiLogs(method, path, body) {
  const token = localStorage.getItem('nebb_token');
  const res = await fetch(`/api${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export default function ImportsPage() {
  const { showToast } = useApp();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [soloPendientes, setSoloPendientes] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try { setLogs(await apiLogs('GET', '/import-logs')); }
    catch (e) { showToast('Error al cargar el historial: ' + e.message, 'error'); }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { cargar(); }, [cargar]);

  const marcarRevisada = async (log) => {
    try {
      const upd = await apiLogs('PUT', `/import-logs/${log.id}`, { reviewed: !log.reviewed });
      setLogs(ls => ls.map(l => l.id === log.id ? upd : l));
    } catch (e) { showToast('Error: ' + e.message, 'error'); }
  };

  const visibles = soloPendientes ? logs.filter(l => !l.reviewed) : logs;
  const resumen = (items) => {
    const c = items.reduce((acc, it) => { acc[it.status] = (acc[it.status] || 0) + 1; return acc; }, {});
    const mm = items.filter(it => it.mismatch).length;
    return { c, mm };
  };

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Revisión de cargas</h1>
          <p className="page-subtitle">{logs.filter(l => !l.reviewed).length} carga{logs.filter(l => !l.reviewed).length !== 1 ? 's' : ''} pendiente{logs.filter(l => !l.reviewed).length !== 1 ? 's' : ''} de revisión</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn btn-sm ${soloPendientes ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSoloPendientes(p => !p)}>
            {soloPendientes ? 'Pendientes' : 'Todas'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={cargar}><RefreshCw size={13} /> Actualizar</button>
        </div>
      </div>

      {loading && <p style={{ color: 'var(--text-muted)', padding: 24 }}>Cargando…</p>}
      {!loading && visibles.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          {soloPendientes ? 'No hay cargas pendientes de revisión.' : 'Todavía no hay cargas registradas.'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {visibles.map(log => {
          const { c, mm } = resumen(log.items || []);
          const open = !!expanded[log.id];
          return (
            <div key={log.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer', flexWrap: 'wrap' }}
                onClick={() => setExpanded(s => ({ ...s, [log.id]: !s[log.id] }))}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>
                    {fmtPatron(log.at, 'dd/MM/yyyy HH:mm')} · {SOURCE_LABEL[log.source] || log.source} · {log.userName}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                    {(log.items || []).length} registro{(log.items || []).length !== 1 ? 's' : ''}
                    {log.reviewed && ` · revisada por ${log.reviewedBy} el ${fmtPatron(log.reviewedAt, 'dd/MM HH:mm')}`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {Object.entries(ST).map(([k, s]) => c[k] ? <span key={k} className={`badge ${s.class}`} style={{ fontSize: 10 }}>{c[k]} {s.label}</span> : null)}
                  {mm > 0 && <span className="badge badge-red" style={{ fontSize: 10 }}>⚠ {mm} mal asignada{mm !== 1 ? 's' : ''}?</span>}
                  {log.reviewed
                    ? <span className="badge badge-green" style={{ fontSize: 10 }}>✓ Revisada</span>
                    : <span className="badge badge-amber" style={{ fontSize: 10 }}>Pendiente</span>}
                  {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                </div>
              </div>

              {open && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '10px 16px 14px' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 11 }}>
                          <th style={{ padding: '6px 8px' }}>Cliente (documento)</th>
                          <th style={{ padding: '6px 8px' }}>Póliza</th>
                          <th style={{ padding: '6px 8px' }}>Compañía</th>
                          <th style={{ padding: '6px 8px' }}>Estado</th>
                          <th style={{ padding: '6px 8px' }}>Detalle</th>
                          <th style={{ padding: '6px 8px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(log.items || []).map((it, i) => {
                          const s = ST[it.status] || ST.skipped;
                          return (
                            <tr key={i} style={{ borderTop: '1px solid var(--border)', background: it.mismatch ? 'rgba(255,80,80,0.05)' : 'transparent' }}>
                              <td style={{ padding: '7px 8px', fontWeight: 500 }}>{it.clientName || '—'}</td>
                              <td style={{ padding: '7px 8px', fontFamily: 'monospace' }}>{it.policyNumber || '—'}</td>
                              <td style={{ padding: '7px 8px' }}>{it.company || '—'}</td>
                              <td style={{ padding: '7px 8px' }}><span className={`badge ${s.class}`} style={{ fontSize: 10 }}>{it.mismatch ? '⚠ Mal asignada?' : s.label}</span></td>
                              <td style={{ padding: '7px 8px', color: it.mismatch ? 'var(--red)' : 'var(--text-secondary)', maxWidth: 300 }}>{it.detail || '—'}</td>
                              <td style={{ padding: '7px 8px', whiteSpace: 'nowrap' }}>
                                {it.existingPolicyId && (
                                  <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/policies/${it.existingPolicyId}`)}>
                                    <FileText size={12} /> Ver póliza
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                    <button className={`btn btn-sm ${log.reviewed ? 'btn-secondary' : 'btn-primary'}`} onClick={() => marcarRevisada(log)}>
                      <CheckCircle size={13} /> {log.reviewed ? 'Marcar como pendiente' : 'Marcar como revisada'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
